import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, getDocs, updateDoc, collection, query, where, limit, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { EmailAuthProvider, reauthenticateWithCredential, signOut } from "firebase/auth";
import { commonPasswords } from "../../services/passwordUtils";
import StaffChangePassword from "../../components/staffcomponents/StaffChangePassword";
import TwoFactorOtpModal from "../../components/otpmodals/TwoFactorOtpModal";
import StaffChangeVerificationModal from "../../components/otpmodals/StaffChangeVerificationModal";
import { useLanguage } from "../../contexts/LanguageContext";
import { ADMIN_ACCOUNT_ID, updateUserEmailByUid } from "../../services/adminService";
import "../AdminPage.css";
import "./staffcss/sharedprofile.css";
import { sessionService } from "../../services/sessionService";
import { apiRevokeSession, apiLogoutAllSessions } from "../../services/sessionApiService";

function AdminProfile() {
  const { language, changeLanguage, getTranslations } = useLanguage();
  const t = getTranslations(true);
  const [editProfile, setEditProfile] = useState(false);
  const [editPassword, setEditPassword] = useState(false);
  const [twoFA, setTwoFA] = useState(true);
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("System Administrator");
  const [email, setEmail] = useState("admin****@example.com");
  const [contactNumber, setContactNumber] = useState("9123456789");
  const [localLanguage, setLocalLanguage] = useState("English");
  const [adminProfile, setAdminProfile] = useState({
    systemId: ADMIN_ACCOUNT_ID,
    createdAt: "January 10, 2026",
    lastLogin: "Today, 10:32 AM",
    status: "Active"
  });

  // Store original values for cancel functionality
  const [originalValues, setOriginalValues] = useState({
    displayName: "System Administrator",
    email: "admin****@example.com",
    contactNumber: "9123456789",
    language: "English"
  });

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [pendingContact, setPendingContact] = useState("");
  const [showContactOtp, setShowContactOtp] = useState(false);
  const [contactOtp, setContactOtp] = useState("");
  const [contactOtpError, setContactOtpError] = useState("");

  // ---- Session management state ----
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokeLoadingId, setRevokeLoadingId] = useState(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [showLogoutAllConfirm, setShowLogoutAllConfirm] = useState(false);
  const currentSessionId = sessionService.getCurrentSessionId();

  const formatSessionTime = (date) => {
    if (!date) return "Unknown";
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const [errors, setErrors] = useState({
    displayName: "",
    email: "",
    contact: "",
  });

  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingContact, setIsCheckingContact] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [pendingTwoFactorValue, setPendingTwoFactorValue] = useState(false);
  const [justSavedProfile, setJustSavedProfile] = useState(false);
  const [emailAvailabilityCache, setEmailAvailabilityCache] = useState({});
  const [contactAvailabilityCache, setContactAvailabilityCache] = useState({});
  const [showPasswordVerificationModal, setShowPasswordVerificationModal] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(null);
  const [showContactVerificationModal, setShowContactVerificationModal] = useState(false);
  const [pendingContactChange, setPendingContactChange] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    // Get firstName and lastName from localStorage since they're not stored in Firestore
    const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    const firstName = storedDraft.firstName || user.firstName || '';
    const lastName = storedDraft.lastName || user.lastName || '';
    const fallbackName =
      [firstName, lastName].filter(Boolean).join(" ") || user.uniqueIdentifierCode || "System Administrator";
    const displayNameValue = user.displayName || fallbackName;
    const emailValue = user.email || "admin****@example.com";
    let contactValue = "9123456789";

    // Use phoneLast10 if available, otherwise extract from phone
    if (user.phoneLast10) {
      contactValue = user.phoneLast10;
    } else if (user.phone) {
      const digits = user.phone.replace(/\D/g, "").slice(-10);
      if (digits) contactValue = digits;
    }

    const languageValue = user.language || "English";
    
    setDisplayName(displayNameValue);
    setEmail(emailValue);
    setContactNumber(contactValue);
    setLocalLanguage(languageValue);

    // Set dynamic admin profile info
    const createdAtDate = user.createdAt ? (typeof user.createdAt === 'string' ? new Date(user.createdAt) : (user.createdAt.toDate ? user.createdAt.toDate() : new Date(user.createdAt))) : new Date();
    const lastLoginDate = user.lastLogin ? (typeof user.lastLogin === 'string' ? new Date(user.lastLogin) : (user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin))) : new Date();

    const formatDateTime = (date) => {
      const dateStr = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase();
      return { date: dateStr, time: timeStr };
    };

    setAdminProfile({
      systemId: user.adminId || user.systemId || ADMIN_ACCOUNT_ID,
      createdAt: formatDateTime(createdAtDate),
      lastLogin: formatDateTime(lastLoginDate),
      status: user.status || (user.deactivated ? "Deactivated" : "Active")
    });
    
    // Set original values for cancel functionality
    setOriginalValues({
      displayName: displayNameValue,
      email: emailValue,
      contactNumber: contactValue,
      language: languageValue
    });

    // Clear availability cache on component load to ensure fresh data
    setEmailAvailabilityCache({});
    setContactAvailabilityCache({});

    if (typeof user.twoFactorEnabled === "boolean") {
      setTwoFA(user.twoFactorEnabled);
    } else {
      // Default to false if missing
      setTwoFA(false);
    }

    // Fetch from Firestore to ensure source of truth
    const fetchTwoFactor = async () => {
      const uid = auth.currentUser?.uid || user.uid;
      if (!uid) return;
      try {
        const docSnap = await getDoc(doc(db, "users", uid));
        if (docSnap.exists()) {
          const remoteTwoFactor = docSnap.data().twoFactorEnabled;
          if (typeof remoteTwoFactor === "boolean") {
            setTwoFA(remoteTwoFactor);
            // Sync local storage
            const updatedUser = { ...user, twoFactorEnabled: remoteTwoFactor };
            localStorage.setItem("user", JSON.stringify(updatedUser));
          }
        }
      } catch (err) {
        console.error("Error fetching 2FA state:", err);
      }
    };
    fetchTwoFactor();
  }, []);

  useEffect(() => {
    // Skip validation if profile was just saved to prevent unnecessary Firestore queries
    if (justSavedProfile) {
      setJustSavedProfile(false);
      return;
    }

    const checkAvailability = async () => {
      const newErrors = {};

      // Display Name validation
      if (!displayName.trim()) {
        newErrors.displayName = "Display Name is required";
      } else if (displayName.length > 50) {
        newErrors.displayName = "Display Name must be 50 characters or less";
      } else if (/[^A-Za-z\s'-]/.test(displayName)) {
        newErrors.displayName = "Display Name contains invalid characters";
      } else {
        newErrors.displayName = "";
      }

      // Email validation
      const emailLower = email.trim().toLowerCase();
      if (!email.trim()) {
        newErrors.email = "Email is required";
      } else if (email.length > 254) {
        newErrors.email = "Email must be 254 characters or less";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors.email = "Invalid email format";
      } else if (emailLower !== originalValues.email.toLowerCase()) {
        // Check cache first to avoid redundant Firestore queries
        if (emailAvailabilityCache[emailLower] !== undefined) {
          newErrors.email = emailAvailabilityCache[emailLower] ? "Email already taken" : "";
        } else {
          setIsCheckingEmail(true);
          const emailSnap = await getDocs(
            query(collection(db, "users"), where("email", "==", emailLower), limit(1))
          );
          const isTaken = !emailSnap.empty;
          // Cache the result
          setEmailAvailabilityCache(prev => ({ ...prev, [emailLower]: isTaken }));
          newErrors.email = isTaken ? "Email already taken" : "";
          setIsCheckingEmail(false);
        }
      } else {
        newErrors.email = "";
      }

      // Contact Number validation
      const phoneLast10 = contactNumber.replace(/\D/g, "").slice(-10);
      if (!contactNumber.trim()) {
        newErrors.contact = "Contact number is required";
      } else if (!/^\d{10}$/.test(contactNumber)) {
        newErrors.contact = "Contact number must be 10 digits";
      } else if (!contactNumber.startsWith("9")) {
        newErrors.contact = "Contact number must start with 9";
      } else if (phoneLast10 !== originalValues.contactNumber) {
        // Check cache first to avoid redundant Firestore queries
        if (contactAvailabilityCache[phoneLast10] !== undefined) {
          newErrors.contact = contactAvailabilityCache[phoneLast10] ? "Contact number already taken" : "";
        } else {
          setIsCheckingContact(true);
          const phoneSnap = await getDocs(
            query(collection(db, "users"), where("phoneLast10", "==", phoneLast10), limit(1))
          );
          const isTaken = !phoneSnap.empty;
          // Cache the result
          setContactAvailabilityCache(prev => ({ ...prev, [phoneLast10]: isTaken }));
          newErrors.contact = isTaken ? "Contact number already taken" : "";
          setIsCheckingContact(false);
        }
      } else {
        newErrors.contact = "";
      }

      setErrors(newErrors);
    };

    const debounceCheck = setTimeout(() => {
      checkAvailability();
    }, 500);

    return () => clearTimeout(debounceCheck);
  }, [displayName, email, contactNumber, originalValues, justSavedProfile, emailAvailabilityCache, contactAvailabilityCache]);

  const lengthValid = newPassword.length >= 8 && newPassword.length <= 24;
  const noSpaces = !/\s/.test(newPassword);
  const noRepeats = !/(.)\\1\\1/.test(newPassword);
  const noCommon = !commonPasswords.includes(newPassword);
  const notOldPassword = newPassword !== currentPassword && newPassword !== "";
  const notDisplayName = newPassword.toLowerCase() !== displayName.toLowerCase();

  const passwordValid =
    lengthValid && noSpaces && noRepeats && noCommon && notOldPassword && notDisplayName;

  const confirmValid = confirmPassword.length > 0 && newPassword === confirmPassword;

  const checkboxValidations = {
    lengthValid,
    hasNumber: /\d/.test(newPassword),
    hasUpperLower: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword),
    hasSymbol: /[!@#$%^&*]/.test(newPassword),
  };
  const passwordStrength = [
    checkboxValidations.lengthValid,
    checkboxValidations.hasUpperLower,
    checkboxValidations.hasNumber,
    checkboxValidations.hasSymbol
  ].filter(Boolean).length;

  const handleContactChange = (value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 10);
    setContactNumber(numericValue);
  };

  const handleDisplayNameChange = (value) => {
    // Only allow letters, spaces, hyphens, and apostrophes
    const filteredValue = value.replace(/[^A-Za-z\s'-]/g, "").slice(0, 50);
    setDisplayName(filteredValue);
  };

  const handleEmailChange = (value) => {
    // Disable space input
    const noSpacesValue = value.replace(/\s/g, "").slice(0, 254);
    setEmail(noSpacesValue);
  };

  const handleCancelEdit = () => {
    // Restore original values
    setDisplayName(originalValues.displayName);
    setEmail(originalValues.email);
    setContactNumber(originalValues.contactNumber);
    setLocalLanguage(originalValues.language);
    setEditProfile(false);
    
    // Clear any errors
    setErrors({
      displayName: "",
      email: "",
      contact: ""
    });
  };

  const isProfileValid = !errors.displayName && !errors.email && !errors.contact;

  const updateTwoFactor = async (nextValue) => {
    try {
      const uid = auth.currentUser?.uid || JSON.parse(localStorage.getItem("user") || "{}")?.uid;
      if (!uid) return;
      await updateDoc(doc(db, "users", uid), { twoFactorEnabled: nextValue, updatedAt: serverTimestamp() });
      const admin = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...admin, twoFactorEnabled: nextValue }));
      setTwoFA(nextValue);
    } catch (err) {
      console.error("Error updating 2FA:", err);
    }
  };

  const handleTwoFactorVerificationSuccess = async (verifiedValue) => {
    await updateTwoFactor(verifiedValue);
    setShowTwoFactorModal(false);
  };

  const handleSaveProfile = async () => {
    if (!isProfileValid) return;
    const uid = auth.currentUser?.uid || JSON.parse(localStorage.getItem("user") || "{}")?.uid;
    if (!uid) return;

    const emailChanged = email.trim().toLowerCase() !== originalValues.email.toLowerCase();
    const phoneLast10 = String(contactNumber || "").replace(/\D/g, "").slice(-10);
    const contactChanged = phoneLast10 !== originalValues.contactNumber;
    const languageChanged = localLanguage !== originalValues.language;

    if (emailChanged || contactChanged) {
      // Use modal instead of navigation
      setPendingContactChange({
        uid,
        oldEmail: originalValues.email,
        newEmail: email.trim().toLowerCase(),
        currentPhone: originalValues.contactNumber ? `+63${originalValues.contactNumber}` : null,
        newPhone: phoneLast10,
        displayName,
        language: localLanguage,
        emailChanged,
        contactChanged,
        role: "admin"
      });
      setShowContactVerificationModal(true);
      return;
    }

    setIsUpdatingProfile(true);
    try {
      const phone = phoneLast10 ? `+63${phoneLast10}` : "";

      await updateDoc(doc(db, "users", uid), {
        displayName,
        language: localLanguage,
        phone,
        phoneLast10,
        updatedAt: serverTimestamp(),
      });

      // Update language in context if changed
      if (languageChanged) {
        await changeLanguage(localLanguage === "Filipino" ? "tl" : "en");
      }

      // Update original values on successful save
      setOriginalValues({
        displayName: displayName,
        email: email,
        contactNumber: contactNumber,
        language: localLanguage
      });

      // Clear availability cache after successful save to prevent stale data
      setEmailAvailabilityCache({});
      setContactAvailabilityCache({});

      // Set flag to prevent validation effect from re-running after save
      setJustSavedProfile(true);

      const admin = JSON.parse(localStorage.getItem("user") || "{}");
      const updated = { ...admin, displayName, language: localLanguage, phone, phoneLast10 };
      localStorage.setItem("user", JSON.stringify(updated));
      setEditProfile(false);
    } catch (err) {
      console.error("Save profile error:", err);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleContactVerificationSuccess = async (result) => {
    setShowContactVerificationModal(false);
    // Contact/email change was successful
    const uid = auth.currentUser?.uid;
    if (uid && pendingContactChange) {
      try {
        const updatePayload = {
          displayName: pendingContactChange.displayName,
          language: pendingContactChange.language,
          updatedAt: serverTimestamp()
        };

        if (pendingContactChange.emailChanged) {
          // Update Firebase Authentication via Cloud Function (admin privileges)
          const idToken = await auth.currentUser.getIdToken();
          await updateUserEmailByUid({ uid, email: pendingContactChange.newEmail, idToken });

          updatePayload.email = pendingContactChange.newEmail;
          updatePayload.emailLower = pendingContactChange.newEmail.toLowerCase();
        }

        if (pendingContactChange.contactChanged) {
          updatePayload.phone = `+63${pendingContactChange.newPhone}`;
          updatePayload.phoneLast10 = pendingContactChange.newPhone;
        }

        await updateDoc(doc(db, 'users', uid), updatePayload);

        // Update localStorage
        const admin = JSON.parse(localStorage.getItem("user") || "{}");
        const updated = { ...admin, ...updatePayload };
        localStorage.setItem("user", JSON.stringify(updated));

        // If email was changed, sign out user
        if (pendingContactChange.emailChanged) {
          await signOut(auth);
          navigate('/login');
          return;
        }

        // If only contact changed, update local state and close edit mode
        setOriginalValues({
          displayName: pendingContactChange.displayName,
          email: originalValues.email,
          contactNumber: pendingContactChange.contactChanged ? pendingContactChange.newPhone : originalValues.contactNumber,
          language: pendingContactChange.language
        });

        if (pendingContactChange.contactChanged) {
          setContactNumber(pendingContactChange.newPhone);
        }

        setEditProfile(false);
      } catch (err) {
        console.error("Contact verification save error:", err);
        alert(err.message || "Failed to update profile. Please try again.");
      }
    }
  };

  const handlePasswordVerificationRequired = (passwordData) => {
    setPendingPasswordChange(passwordData);
    setShowPasswordVerificationModal(true);
  };

  const handlePasswordVerificationSuccess = async (result) => {
    setShowPasswordVerificationModal(false);
    // Password change was successful, user will be signed out by the modal
    // Navigate to login page
    navigate('/login');
  };

  const handleVerifyContactOtp = async () => {
    setContactOtpError("OTP flow removed (Firebase-only)");
  };

  const handlePasswordUpdate = async () => {
    const user = auth.currentUser;
    if (!user?.email) return;
    if (!passwordValid || !confirmValid) return;

    setIsUpdatingPassword(true);
    try {
      // Reauthenticate first
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, cred);

      // Trigger password verification via modal
      setPendingPasswordChange({
        uid: user.uid,
        email: user.email,
        currentPassword: currentPassword,
        newPassword: newPassword,
      });
      setShowPasswordVerificationModal(true);

    } catch (err) {
      console.error("Password update error:", err);
      alert(err.message || "Incorrect current password");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // ---- Session subscription & revocation detection ----
  // Use onAuthStateChanged so we wait for Firebase to restore auth state.
  // auth.currentUser can be null on first render even when the user is signed in.
  useEffect(() => {
    let unsubSessions = null;
    let unsubRevocation = null;

    const unsubAuth = auth.onAuthStateChanged((firebaseUser) => {
      if (unsubSessions) { unsubSessions(); unsubSessions = null; }
      if (unsubRevocation) { unsubRevocation(); unsubRevocation = null; }

      if (!firebaseUser?.uid) return;
      const uid = firebaseUser.uid;

      setSessionsLoading(true);
      unsubSessions = sessionService.watchSessions(uid, (sessions) => {
        setActiveSessions(sessions);
        setSessionsLoading(false);
      });

      unsubRevocation = sessionService.watchCurrentSession(uid, async () => {
        const isLogoutInProgress = sessionStorage.getItem('logoutInProgress');
        if (isLogoutInProgress) return;
        sessionStorage.setItem('logoutInProgress', 'true');
        sessionService.clearCurrentSession();
        try { await signOut(auth); } catch (_) {}
        localStorage.clear();
        navigate('/login', { replace: true, state: { message: 'Your session has been revoked. Please sign in again.', type: 'error' } });
        setTimeout(() => sessionStorage.removeItem('logoutInProgress'), 1000);
      });
    });

    return () => {
      unsubAuth();
      if (unsubSessions) unsubSessions();
      if (unsubRevocation) unsubRevocation();
    };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Session handlers ----
  const handleRevokeSession = async (sessionId) => {
    if (revokeLoadingId) return;
    setRevokeLoadingId(sessionId);
    try {
      await apiRevokeSession(sessionId);
    } catch (err) {
      console.error('[AdminProfile] revokeSession error:', err);
    } finally {
      setRevokeLoadingId(null);
    }
  };

  const handleLogoutAllClick = () => setShowLogoutAllConfirm(true);

  const handleLogoutAllConfirm = async () => {
    setShowLogoutAllConfirm(false);
    setLogoutAllLoading(true);
    try {
      await apiLogoutAllSessions();
      sessionService.clearCurrentSession();
      sessionStorage.setItem('logoutInProgress', 'true');
      await signOut(auth);
      localStorage.clear();
      navigate('/login', { replace: true, state: { message: 'You have been signed out of all sessions.', type: 'success' } });
    } catch (err) {
      console.error('[AdminProfile] logoutAllSessions error:', err);
      setLogoutAllLoading(false);
    }
  };

  return (
    <div className="psp-main">
      <div className="psp-grid">
        {/* Left column */}
        <div className="psp-left">
          {/* Account Information */}
          <section className="psp-section">
            <h2 className="psp-section-title">{t.accountSettings}</h2>
            <div className="psp-field">
              <label>Admin ID</label>
              <input type="text" value={adminProfile.systemId} readOnly />
            </div>
            <div className="psp-field">
              <label>{t.fullName}</label>
              <input type="text" value={displayName} readOnly />
            </div>
          </section>

          {/* Change Password */}
          <section className="psp-section">
            <StaffChangePassword userData={{
              fullName: displayName,
              email: email,
              phone: `+63${contactNumber}`,
              dateCreated: adminProfile.createdAt,
              lastLogin: adminProfile.lastLogin,
              language: language
            }} isAdmin={true} onPasswordVerificationRequired={handlePasswordVerificationRequired} />
          </section>
        </div>

        {/* Right column */}
        <div className="psp-right">
          {/* Contact Information box */}
          <div className="psp-box">
            <div className="psp-section-header">
              <h2 className="psp-section-title">{t.personalInformation}</h2>
              {!editProfile && (
                <button
                  type="button"
                  className="psp-btn-edit"
                  onClick={() => setEditProfile(true)}
                >
                  {t.editProfile}
                </button>
              )}
            </div>
            <div className="psp-field">
              <label>{t.email}</label>
              <input
                type="email"
                value={email}
                readOnly={!editProfile}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={errors.email ? "error" : ""}
              />
            </div>
            {errors.email && (
              <p style={{ color: "#dc2626", fontSize: "14px", margin: "4px 0 0" }}>
                {errors.email}
              </p>
            )}
            <div className="psp-field">
              <label>{t.phoneNumber}</label>
              {editProfile ? (
                <div style={{ display: "flex", width: "100%" }}>
                  <span style={{
                    background: "#eff6ff",
                    padding: "12px 16px",
                    borderRadius: "12px 0 0 12px",
                    border: "1px solid #e2e8f0",
                    borderRight: "none",
                    color: "#1e40af",
                    fontWeight: "600"
                  }}>+63</span>
                  <input
                    type="text"
                    value={contactNumber}
                    placeholder="9123456789"
                    onChange={(e) => handleContactChange(e.target.value)}
                    className={errors.contact ? "error" : ""}
                    style={{
                      borderRadius: "0 12px 12px 0",
                      flex: 1,
                      borderLeft: "none"
                    }}
                  />
                </div>
              ) : (
                <input type="text" value={`+63 ${contactNumber}`} readOnly />
              )}
            </div>
            {errors.contact && (
              <p style={{ color: "#dc2626", fontSize: "14px", margin: "4px 0 0" }}>
                {errors.contact}
              </p>
            )}
            <div className="psp-field">
              <label>{t.preferredLanguage}</label>
              {editProfile ? (
                <select
                  value={localLanguage}
                  onChange={(e) => setLocalLanguage(e.target.value)}
                >
                  <option>{t.english}</option>
                  <option>{t.filipino}</option>
                </select>
              ) : (
                <input type="text" value={localLanguage} readOnly />
              )}
            </div>
            {editProfile && (
              <div className="psp-edit-actions">
                <button
                  type="button"
                  className="psp-btn-outline"
                  onClick={handleCancelEdit}
                  disabled={isUpdatingProfile || isCheckingEmail || isCheckingContact}
                >
                  {t.cancel}
                </button>
                <button
                  type="button"
                  className="psp-btn-primary"
                  onClick={handleSaveProfile}
                  disabled={!isProfileValid || isUpdatingProfile || isCheckingEmail || isCheckingContact}
                >
                  {isUpdatingProfile ? t.loading : (isCheckingEmail || isCheckingContact) ? t.loading : t.saveChanges}
                </button>
              </div>
            )}
          </div>

          {/* Access & Security box */}
          <div className="psp-box">
            <h2 className="psp-section-title">Access & Security</h2>
            <div className="psp-toggle-row">
              <div>
                <div className="psp-toggle-label">Two-Factor Authentication</div>
                <div className="psp-toggle-desc" style={{ textAlign: 'left' }}>Manage how your account is verified during login using one-time password (OTP) authentication.</div>
              </div>
              <button
                type="button"
                className={`psp-toggle ${twoFA ? "psp-toggle-on" : ""}`}
                onClick={() => {
                  setPendingTwoFactorValue(!twoFA);
                  setShowTwoFactorModal(true);
                }}
                aria-label="Toggle two-factor authentication"
              >
                <span className="psp-toggle-knob" />
              </button>
            </div>
            {/* Account Information */}
            <div className="psp-account-info">
              <div className="psp-account-info-header">
                <h3 className="psp-account-info-title">Account Information</h3>
              </div>
              <div className="psp-row-fields">
                <div className="psp-field">
                  <label>{t.dateCreated}</label>
                  <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                    <div>{adminProfile.createdAt.date}</div>
                    <div style={{ fontSize: '0.85em', color: '#64748b' }}>{adminProfile.createdAt.time}</div>
                  </div>
                </div>
                <div className="psp-field">
                  <label>{t.lastLogin}</label>
                  <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                    <div>{adminProfile.lastLogin.date}</div>
                    <div style={{ fontSize: '0.85em', color: '#64748b' }}>{adminProfile.lastLogin.time}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="psp-sessions">
              <div className="psp-sessions-header">
                <div className="psp-sessions-label">Where You're Logged In</div>
                <button
                  type="button"
                  className="psp-btn-revoke psp-btn-logout-all"
                  style={{ backgroundColor: 'transparent', borderColor: '#dc2626', color: '#dc2626' }}
                  onClick={handleLogoutAllClick}
                  disabled={logoutAllLoading}
                >
                  {logoutAllLoading ? "Signing out…" : "Log Out All Sessions"}
                </button>
              </div>

              {showLogoutAllConfirm && (
                <div className="psp-session-confirm-overlay" role="dialog" aria-modal="true" aria-label="Confirm log out all sessions">
                  <div className="psp-session-confirm-box">
                    <p>This will sign you out of <strong>all devices</strong>. Continue?</p>
                    <div className="psp-session-confirm-actions" style={{ justifyContent: 'center' }}>
                      <button
                        type="button"
                        className="psp-btn-revoke"
                        style={{ borderColor: '#2563eb', color: '#2563eb', backgroundColor: 'transparent' }}
                        onClick={() => setShowLogoutAllConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="psp-btn-revoke"
                        style={{ borderColor: '#dc2626', color: '#dc2626', backgroundColor: 'transparent' }}
                        onClick={handleLogoutAllConfirm}
                      >
                        Yes, log out all
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {sessionsLoading ? (
                <p style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>Loading sessions…</p>
              ) : activeSessions.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>No active sessions found.</p>
              ) : (
                <ul className="psp-session-list" style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                  {activeSessions.map((s) => {
                    const isCurrent = s.sessionId === currentSessionId;
                    const isRevoking = revokeLoadingId === s.sessionId;
                    const lastActive = s.lastActiveAt?.toDate?.() ?? null;
                    const lastActiveLabel = lastActive ? formatSessionTime(lastActive) : "Unknown";
                    return (
                      <li key={s.sessionId} className={`psp-session-item${isCurrent ? " psp-session-item--current" : ""}`}>
                        <div className="psp-session-info">
                          <span className="psp-session-device">
                            {s.deviceName || `${s.browser || "Unknown"} on ${s.operatingSystem || "Unknown"}`}
                            {isCurrent && (
                              <span style={{ marginLeft: '8px', fontSize: '0.72rem', background: '#dcfce7', color: '#15803d', borderRadius: '4px', padding: '1px 6px', fontWeight: 600 }}>
                                Current session
                              </span>
                            )}
                          </span>
                          <span className="psp-session-location" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            {s.deviceType || "Desktop"} · Last active: {lastActiveLabel}
                          </span>
                        </div>
                        {!isCurrent && (
                          <button
                            type="button"
                            className="psp-btn-revoke"
                            onClick={() => handleRevokeSession(s.sessionId)}
                            disabled={isRevoking || logoutAllLoading}
                            aria-label={`Revoke session on ${s.deviceName}`}
                          >
                            {isRevoking ? "Revoking…" : "Revoke"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {showContactOtp && (
        <div className="admin-aura-modal-overlay">
          <div className="admin-aura-delete-modal" style={{ border: "2px solid #4a90e2" }}>
            <h3 className="modal-title">Verify Contact Number</h3>
            <p className="modal-text">Enter the OTP sent to your contact number.</p>
            <input
              className="cs-aura-input"
              value={contactOtp}
              onChange={(e) => setContactOtp(e.target.value)}
              placeholder="Enter OTP"
            />
            {contactOtpError && <p className="error-msg">{contactOtpError}</p>}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowContactOtp(false)}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleVerifyContactOtp}>
                Verify
              </button>
            </div>
          </div>
        </div>
      )}

      <TwoFactorOtpModal
        isOpen={showTwoFactorModal}
        onClose={() => setShowTwoFactorModal(false)}
        targetValue={pendingTwoFactorValue}
        onSuccess={handleTwoFactorVerificationSuccess}
        userEmail={email}
        userPhone={`+63${contactNumber}`}
        userFirstName={displayName}
      />

      <StaffChangeVerificationModal
        isOpen={showContactVerificationModal}
        onClose={() => setShowContactVerificationModal(false)}
        flow="contact-change"
        pendingContactChange={pendingContactChange}
        onSuccess={handleContactVerificationSuccess}
        onCancel={() => {
          setShowContactVerificationModal(false);
        }}
      />

      <StaffChangeVerificationModal
        isOpen={showPasswordVerificationModal}
        onClose={() => setShowPasswordVerificationModal(false)}
        flow="password-change"
        pendingPasswordChange={pendingPasswordChange}
        onSuccess={handlePasswordVerificationSuccess}
        onCancel={() => {
          setShowPasswordVerificationModal(false);
        }}
      />
    </div>
  );
}

export default AdminProfile;
